import { Controller } from "@hotwired/stimulus";

// Reports page — progressive enhancement only. The page is fully server-rendered
// and works without JS (preset links navigate; the search + filter forms have a
// real submit). This controller just removes the extra click:
//   * changing the event-type filter submits its form immediately
//   * typing in search submits after a short debounce
//   * picking both custom-range dates submits the range form
// Null-guarded throughout so a missing target never throws.
export default class extends Controller {
  static targets = ["search", "typeFilter", "customForm", "startDate", "endDate"];
  static values = { expand: String };

  connect() {
    this.searchTimer = null;
    // Deep-link: ?expand=<event_id> opens that event's breakdown on load (and
    // scrolls it into view) so Playwright / a shared link lands pre-expanded,
    // without a click. The drill button carries data-event-id matching the value.
    if (this.expandValue) {
      const btn = this.element.querySelector(
        `.rp-drill[data-event-id="${this.expandValue}"]`
      );
      if (btn && btn.getAttribute("aria-expanded") !== "true") {
        this.openDrill(btn);
        btn.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }
  }

  // Toggle one event row's nested breakdown panel. The panel's <turbo-frame> is
  // loading="lazy", so revealing it triggers the fetch automatically the first
  // time — we never re-fetch on a later open.
  toggleDrill(event) {
    const btn = event.currentTarget;
    const expanded = btn.getAttribute("aria-expanded") === "true";
    if (expanded) {
      this.closeDrill(btn);
    } else {
      this.openDrill(btn);
    }
  }

  openDrill(btn) {
    const panel = this.panelFor(btn);
    if (!panel) return;
    btn.setAttribute("aria-expanded", "true");
    panel.hidden = false;
  }

  closeDrill(btn) {
    const panel = this.panelFor(btn);
    if (!panel) return;
    btn.setAttribute("aria-expanded", "false");
    panel.hidden = true;
  }

  panelFor(btn) {
    const id = btn.getAttribute("aria-controls");
    return id ? document.getElementById(id) : null;
  }

  // Auto-submit the filter form when the event-type filter changes. The select
  // is upgraded to select2 (see index.html.erb / the .select2-search-off
  // wrapper), so we listen on BOTH the native `change` and select2's own
  // `select2:select` / `select2:unselect` events. select2 4.x does dispatch a
  // native `change` on the underlying <select>, but it's a jQuery-triggered
  // event; binding select2:* via jQuery (when available) guarantees we catch
  // the selection regardless of how the event propagates. Mirrors the
  // Financials Orders status filter, which uses select2:select/unselect.
  typeFilterTargetConnected(el) {
    el.addEventListener("change", () => this.submitForm(el));
    if (window.jQuery) {
      window.jQuery(el).on("select2:select select2:unselect", () => this.submitForm(el));
    }
  }

  // Debounced submit as the organizer types in the search box.
  searchTargetConnected(el) {
    el.addEventListener("input", () => {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.submitForm(el), 450);
    });
  }

  // Submit the custom-range form once both dates are present.
  customRangeChanged() {
    if (!this.hasCustomFormTarget) return;
    const start = this.hasStartDateTarget ? this.startDateTarget.value : "";
    const end = this.hasEndDateTarget ? this.endDateTarget.value : "";
    if (start && end) this.customFormTarget.requestSubmit();
  }

  startDateTargetConnected(el) {
    el.addEventListener("change", () => this.customRangeChanged());
  }

  endDateTargetConnected(el) {
    el.addEventListener("change", () => this.customRangeChanged());
  }

  submitForm(el) {
    const form = el.closest("form");
    if (form) form.requestSubmit();
  }
}
